import React from "react"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import { useAuth } from "@clerk/clerk-react"
import useLoggedInUser from "../hooks/use-logged-in-user"
import { useRefinedAccess } from "../hooks/useRefinedAccess"
import { isPublicRoute } from "../utils/routes"
import { Alert, Button, Stack } from "@chakra-ui/react"
import { AccountApiError } from "../api/account"

const MyLearning = React.lazy(() => import("./learning/MyLearning"))
const LearnerInvitation = React.lazy(() => import("./learning/LearnerInvitation"))

const ClerkLogin = React.lazy(() => import("./Login/ClerkLogin"))
const Dashboard = React.lazy(() => import("./dashboard/Dashboard"))
const AdminUsers = React.lazy(() => import("./dashboard/AdminUsers"))
const UserDetail = React.lazy(() => import("./userDetail/UserDetail"))
const QRCodeRedirect = React.lazy(() => import("./qrCode/QRCodeRedirect"))
const PartnerEmployeeRedirect = React.lazy(() => import("./qrCode/PartnerEmployeeRedirect"))
const PartnerBadges = React.lazy(() => import("./organizations/PartnerBadges"))
const PrivacyPolicy = React.lazy(() => import("./privacyPolicy/PrivacyPolicy"))
const AccessDenied = React.lazy(() => import("./AccessDenied"))
const PrintQueue = React.lazy(() => import("./printQueue/PrintQueue"))
const QRPrintAudit = React.lazy(() => import("./qrPrintAudit/QRPrintAudit"))
const GrowAdmin = React.lazy(() => import("./grow/GrowAdmin"))

const LoadingScreen = () => (
  <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#5D82ED] to-[#C08AF8]">
    <div className="text-center">
      <div className="flex items-center justify-center gap-2">
        <div className="h-2 w-2 bg-white rounded-full animate-bounce [animation-delay:-0.3s]" />
        <div className="h-2 w-2 bg-white rounded-full animate-bounce [animation-delay:-0.15s]" />
        <div className="h-2 w-2 bg-white rounded-full animate-bounce" />
      </div>
    </div>
  </div>
)

export default function Router({
  sessionError = false,
  scope,
}: {
  sessionError?: boolean
  scope?: string
}) {
  const location = useLocation()
  const loggedInUser = useLoggedInUser()
  const { isSignedIn, isLoaded: isAuthLoaded } = useAuth()

  // Only check refined access for non-public routes when signed in
  const isPublic = isPublicRoute(location.pathname)
  const isInvitation = location.pathname === "/learner-invitation"
  const {
    account,
    accessDenied,
    isLoading: accessLoading,
    error,
    retry,
  } = useRefinedAccess(!isPublic && !isInvitation)

  // 1. Wait for Clerk to load
  if (!isAuthLoaded) {
    return <LoadingScreen />
  }

  // Acceptance is authenticated, but intentionally precedes approved-membership gating.
  if (isInvitation) return <LearnerInvitation />

  // 2. Public routes remain accessible. Only the profile has administrative
  //    local state to discard when authority changes; QR resolution must not
  //    remount when a pending /me query settles.
  if (isPublic) {
    return (
      <React.Fragment>
        <Routes>
          <Route path="/login/*" Component={ClerkLogin} />
          <Route path="/privacyPolicy" Component={PrivacyPolicy} />
          <Route path="/user/:partnerSlug/:employeeId" Component={PartnerEmployeeRedirect} />
          <Route
            path="/user/:userID"
            element={<UserDetail key={scope} sessionError={sessionError} />}
          />
          <Route path="/q/:code/*" Component={QRCodeRedirect} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </React.Fragment>
    )
  }

  // 3. Not signed in on protected route -> redirect to login
  if (!isSignedIn) {
    return <Navigate to="/login" replace />
  }

  // 4. User is signed in but doesn't have access to Refined
  if (accessDenied) {
    if (!(error instanceof AccountApiError && error.status === 403)) {
      return (
        <Stack p={8}>
          <Alert status="error">Unable to verify your access. No account data is available.</Alert>
          <Button onClick={() => retry()}>Try again</Button>
        </Stack>
      )
    }
    return (
      <React.Suspense fallback={<LoadingScreen />}>
        <AccessDenied />
      </React.Suspense>
    )
  }

  // Session failures must not block public QR views or invitation acceptance.
  if (sessionError)
    return (
      <Stack p={8}>
        <Alert status="error">Unable to establish your session. Please sign in again.</Alert>
        <Button onClick={() => window.location.assign("/login")}>Sign in</Button>
      </Stack>
    )

  // 5. Signed in but local user context not yet synced -> Show loading
  if (!account || !loggedInUser || accessLoading) {
    return <LoadingScreen />
  }

  // 6. Fully authenticated -> Show protected routes. Key only this protected
  //    subtree by scope so account/organization changes isolate query caches
  //    and UI state here, without unmounting public-route subtrees.
  return (
    <React.Fragment key={scope}>
      <Routes>
        <Route
          path="/learning"
          element={account.roles.includes("learner") ? <MyLearning /> : <Navigate to="/" replace />}
        />
        <Route
          path="/"
          element={account.isAdmin ? <Dashboard /> : <Navigate to="/learning" replace />}
        />
        <Route
          path="/admins"
          element={account.isAdmin ? <AdminUsers /> : <Navigate to="/learning" replace />}
        />
        <Route
          path="/partner-badges"
          element={
            account.isAdmin && account.isSuperAdmin ? (
              <PartnerBadges />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/print-queue"
          element={account.isAdmin ? <PrintQueue /> : <Navigate to="/learning" replace />}
        />
        <Route
          path="/qr-print-audit"
          element={account.isAdmin ? <QRPrintAudit /> : <Navigate to="/learning" replace />}
        />
        <Route
          path="/grow"
          element={account.isAdmin ? <GrowAdmin /> : <Navigate to="/learning" replace />}
        />
        <Route path="/user/:userID" Component={UserDetail} />
        <Route path="/q/:code/*" Component={QRCodeRedirect} />
        <Route path="/privacyPolicy" Component={PrivacyPolicy} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Fragment>
  )
}
